import { Controller } from '@hotwired/stimulus'
import { log } from '../../helpers/debug'
import computedValues from '../../helpers/computedValues'
import {
  connectFlowValue,
  connectPageRequirements,
  connectUserData,
  getComponentContext,
  getDispatchUpdateFn,
  getFlow,
  getPageNavHandlers,
  shouldDisableInteractionDuringTransition,
  lifecycleTrigger,
  getUserDataFn,
  doNotStoreComponentDataInCloud,
} from '../../helpers/controllerHelpers/utils'
import { TEST_MODE } from '../../helpers/utils'
import { CustomButton, UserData, UserDataValue } from '../../api/models/types'
import contextFunctionsHelpers from '../../helpers/contextFunctionsHelpers'
import jitsu from '../../helpers/jitsu'

export default class extends Controller {
  static values = {
    action: String,
    // isValid: Boolean,
    infoBox: String,
    skipValidation: Boolean,
    requireNavTarget: Boolean,
    requireValidPage: Boolean,
    stateOnValid: { type: String, default: 'disable' },
  }
  disconnectPageRequirements?: (() => void) | undefined
  disconnectUserData?: (() => void) | undefined
  _disconnectPageId?: (() => void) | undefined
  component: CustomButton
  pageId: string
  flowId: string
  declare stateOnValidValue: string
  disableButtonState: boolean
  declare actionValue: string
  declare skipValidationValue: string
  declare requireNavTargetValue: boolean
  loading: boolean
  declare requireValidPageValue: boolean
  // declare isValidValue: boolean
  declare infoBoxValue: string
  declare userDataValue: UserDataValue
  isValidValue: boolean
  flowEl: Element | null

  connect() {
    this.disconnectPageRequirements = connectPageRequirements(this)
    this.disconnectUserData = connectUserData(this)

    this._disconnectPageId = connectFlowValue(
      this,
      { attributeName: 'data-flow-page-id-value', outputValue: 'pageId' },
      this.hideIfMissingNavTarget
    )
    const { component, pageId, flowId, flowEl } = getComponentContext(this)
    this.component = component as CustomButton
    this.pageId = pageId
    this.flowId = (flowId as string) || ''
    this.flowEl = flowEl
    this.element.setAttribute('controller-custom-button-connected', 'true')
    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectPageRequirements === 'function') this.disconnectPageRequirements()
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this._disconnectPageId === 'function') this._disconnectPageId()
  }

  userDataValueChanged(newData: { [x: string]: string }) {
    const key = this.component?.key
    if (!key) return

    const value = newData[key]

    if (this.component.toggle) {
      value ? this.element.classList.add('selected') : this.element.classList.remove('selected')
    }
  }

  isValidValueChanged(newValidState: boolean, oldValidState: boolean) {
    if (newValidState === oldValidState) return
    log(
      'isValidValueChanged() this.pageId, newValidState, oldValidState',
      this.pageId,
      newValidState,
      oldValidState
    )
    if (
      (this.actionValue === 'next-page' && !this.skipValidationValue) ||
      this.requireValidPageValue
    ) {
      switch (this.stateOnValidValue) {
        case 'hide': {
          newValidState ? this.showButton() : this.hideButton()
          break
        }

        case 'always_show': {
          this.disableButtonState = true
          this.showButton()
          this.enableButton()
          this.visibleButton()
          break
        }

        default: {
          //* Disable the button by default if no setting or if not one of the above cases */
          newValidState ? this.enableButton() : this.disableButton()
          break
        }
      }
    }
  }

  async evaluateRunningActions(event) {
    if ((event as KeyboardEvent).key === ' ') event.preventDefault()
    if ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') {
      this.performButtonAction(event)
    }
  }

  async performButtonAction(event: { preventDefault: () => void }) {
    event.preventDefault()
    if (shouldDisableInteractionDuringTransition(this)) return

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component.id
    )
    window.Savvy.addEventLogs?.([
      {
        flow_id: this.flowId,
        event_type: 'button:clicked',
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue as unknown as UserData,
        data: {
          component_id: this.component.id,
          component_key: this.component.key,
          component_path: [
            ...(this.pageId ? ['pages', `${pageIndex}`] : []),
            'components',
            `${componentIndex}`,
          ],
        },
      },
    ])

    if (this.element.getAttribute('disabled') === 'disabled') {
      this.dispatch('validate')
      return
    }

    const actions = this.component.outputs_onclick
    //LUCIAN added next lines
    if (this.component.show_loading_until_actions_resolved === true) {
      //We cant just use this.element.innerText here because the text is inside another div, this.element could have a bunch of inner divs
      const mainContainer = this.element as HTMLElement

      const innerTextElement = mainContainer.querySelector('.text') as HTMLElement
      if (innerTextElement) {
        this.element.setAttribute('disabled', 'disabled')
        this.component.loading_message
          ? (innerTextElement.innerText = this.component.loading_message)
          : (innerTextElement.innerText = 'Loading...')
      }
    }
    //END of LUCIANs addition

    if (Array.isArray(actions))
      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.component,
          //LUCIAN added next lines
          ...(this.component.show_loading_until_actions_resolved
            ? { callback: (status) => this.finishedAction(status) }
            : {}),
          //END of LUCIANs addition
        },
      })

    this.dispatch('actions', {
      detail: {
        actions: (this.component.conversions || []).map((c) => ({ output: 'conversion', ...c })),
        component: this.component,
      },
    })

    if (this.component.use_custom_code && this.component.custom_code) {
      const res = await this.runCustomCode()
      if (!res) return
    }
    if (this.component.toggle) {
      const key = this.component.key
      this.dispatch('update', { detail: { key, value: !this.userDataValue[key] } })
    } else this.doAction(this.actionValue)

    if (this.component.track_clicks && !doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track('button:clicked', {
        flow_id: this.flowId,
        button_id: this.component.id,
        button_key: this.component.key,
      })
    }
  }

  //LUCIAN added next lines
  finishedAction(status: string) {
    const mainContainer = this.element as HTMLElement
    const innerTextElement = mainContainer.querySelector('.text') as HTMLElement
    if (innerTextElement && this.component.text !== undefined) {
      this.element.removeAttribute('disabled')
      if (status === 'fail') {
        this.component.fail_message
          ? (innerTextElement.innerText = this.component.fail_message)
          : (innerTextElement.innerText = 'Action failed...')
      } else {
        this.component.success_message
          ? (innerTextElement.innerText = this.component.success_message)
          : (innerTextElement.innerText = this.component.text)
      }
    }
  }
  //END of LUCIANs addition

  hideIfMissingNavTarget(pageId: string) {
    if (!this.requireNavTargetValue) return
    const action = this.actionValue
    const currentPageId = pageId
    const pages = getFlow(this).pages || []
    const currentPageIndex = pages.findIndex((p: { id }) => p.id === currentPageId)
    let isAllowed: boolean
    switch (action) {
      case 'next-page': {
        isAllowed = currentPageIndex < pages.length - 1
        break
      }
      case 'prev-page': {
        isAllowed = currentPageIndex > 0
        break
      }
      default:
        isAllowed = true
        break
    }

    isAllowed ? this.showButton() : this.hideButton()
  }

  disableButton() {
    if (this.disableButtonState) return
    this.element.setAttribute('disabled', 'disabled')
  }

  enableButton() {
    this.element.removeAttribute('disabled')
  }

  invisibleButton() {
    if (this.disableButtonState) return
    ;(this.element as HTMLElement).style.visibility = 'hidden'
    ;(this.element as HTMLElement).style.pointerEvents = 'none'
  }

  visibleButton() {
    ;(this.element as HTMLElement).style.visibility = ''
    ;(this.element as HTMLElement).style.pointerEvents = ''
  }

  hideButton() {
    if (this.disableButtonState) return
    if (this.element.getAttribute('data-controller')?.split(' ').includes('condition')) {
      this.element.setAttribute('data-condition-override-value', 'true')
    } else {
      ;(this.element as HTMLElement).style.display = 'none'
    }
  }

  showButton() {
    if (this.element.getAttribute('data-controller')?.split(' ').includes('condition')) {
      this.element.setAttribute('data-condition-override-value', 'false')
    } else {
      ;(this.element as HTMLElement).style.display = ''
    }
  }

  setLoading(state: boolean) {
    /* NYI - loading states */
    this.loading = state
    // state ? this.disableButton() : this.enableButton()
  }

  doAction(action: string) {
    const reqValidation = this.requireValidPageValue
    if (reqValidation && !this.isValidValue) {
      this.dispatch('validate')
      return
    }
    switch (action) {
      case 'open-url': {
        /* Element should be a a-tag so we just ignore here - simplifying case */
        // if (!context.openInSameTab) break
        const attr = this.element.getAttribute('href') || ''
        const href = computedValues(attr, this.userDataValue)
        if (TEST_MODE) this.element.setAttribute('href', href)
        window.open(href, String(this.element.getAttribute('target')))
        break
      }
      case 'open-info-box': {
        this.dispatch('info-box', { detail: { value: this.infoBoxValue } })
        break
      }
      case 'next-page': {
        this.dispatch('next', {
          detail: { isButton: true, skipValidation: this.skipValidationValue, value: this.pageId },
        })
        break
      }
      case 'close-popup': {
        this.dispatch('close-popup')
        break
      }
      case 'reset': {
        this.dispatch('reset')
        break
      }
      case 'prev-page': {
        this.dispatch('prev', {
          detail: { isButton: true },
        })
        break
      }
      default: {
        const starterString = 'page-'
        if ((this.actionValue || '').startsWith(starterString)) {
          const pageId = this.actionValue.slice(starterString.length)
          this.dispatch('to-page', { detail: { value: pageId } })
        }
        break
      }
    }
  }

  async runCustomCode() {
    const setUserData = getDispatchUpdateFn(this)
    const getUserData = getUserDataFn(this)
    const resetUserData = () => this.dispatch('reset')
    const openInfoBox = (key: string) => this.dispatch('info-box', { detail: { value: key } })
    const triggerValidation = () => this.dispatch('validate')
    // `this` does not refer to the controller if used inside the contextFunctions object directly,
    // it will instead refer to the `contextFunctions` object itself.
    const triggerAction = (action: string) => this.doAction(action)
    const contextFunctions = {
      getUserData,
      setUserData,
      resetUserData,
      triggerAction,
      getComponentElement: () => this.element,
      openInfoBox,
      triggerValidation,
      helpers: contextFunctionsHelpers,
      ...getPageNavHandlers(this),
    }
    const key = this.component.key
    this.setLoading(true)
    let finalRes: boolean

    try {
      const code = `${this.component.custom_code}; customHandler(${JSON.stringify(
        this.userDataValue
      )}, contextFunctions);`
      /* Custom Code must return an object with shape {result: any, success: boolean} */
      const result = await eval(code)

      if (result) {
        const updateObject = { detail: { key, value: result.success }, params: {} }
        if (this.component.output_key) {
          updateObject.params = { key: this.component.output_key, value: result.value }
        }
        this.dispatch('update', updateObject)
      }
      finalRes = result ? result.success : false
    } catch (error) {
      console.error(error)
      const updateObject = { detail: { key, value: false }, params: {} }
      if (this.component.output_key) {
        const errorContext = (error.response && error.response.data) || error.message
        updateObject.params = { key: this.component.output_key, value: errorContext }
      }
      this.dispatch('update', updateObject)
      finalRes = false
    }
    this.setLoading(false)
    return finalRes
  }
}
